import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { type Selection } from 'react-aria-components';
import { useFieldArray, useForm } from 'react-hook-form';
import { useListData } from 'react-stately';
import { Button } from '@module/mdrt-org/shared/components/button/button';
import { Dialog } from '@module/mdrt-org/shared/components/dialog/dialog';
import { useUserContext } from '@module/mdrt-org/shared/components/providers/user-context';
import { TagsList } from '@module/mdrt-org/shared/components/tags-list/tags-list';
import { type InterestsData } from '@module/mdrt-org/shared/utils/data/graphql/utils/get-topics';
import { patchAccountSettingsAction } from '@module/mdrt-org/shared/utils/data/patch-account-settings-action';
import { AccountSettingsFormStep } from '@module/mdrt-org/shared/utils/enums/account-settings-form-step';
import { AccountSettingsMutationKey } from '@module/mdrt-org/shared/utils/enums/account-settings-mutation-key';
import { splitByCharacterLimit } from '@module/mdrt-org/shared/utils/helpers/split-by-character-limit';
import { useAddDefaultErrorToast } from '@module/mdrt-org/shared/utils/hooks/use-add-default-error-toast';
import { useMutationHandler } from '@module/mdrt-org/shared/utils/hooks/use-mutation-handler';
import { type InterestsFormModalCmsContent } from '@module/mdrt-org/shared/utils/types/interests-form-modal-cms-content';
import { type UserInterestsSettingsPreferenceData, type UserInterestsSettingsPreferenceFormData } from '@module/mdrt-org/shared/utils/types/user-interests-settings-preference';
import { ButtonVariant } from '@shared/ui-components/button/buttonVariant';
import { FormWrapper } from '@shared/ui-components/form-wrapper/form-wrapper';
import { formBaseConfig } from '@shared/utils/constants/formBaseConfig';
import { GTM_ACTION_CLICK, GTM_EVENT_TOPIC_INTEREST_CLICK, GTM_EVENT_USER_INTERACTIONS } from '@shared/utils/constants/gtm';
import { ButtonType } from '@shared/utils/enums/button-type';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import { type ServerActionResponse } from '@shared/utils/types/server-action';
import styles from '@module/mdrt-org/shared/components/interests-form-dialog/interests-form-modal-dialog.module.scss';
type InterestsSectionProps = {
  allTopics: InterestsData;
  cmsContent: InterestsFormModalCmsContent;
  customerInterests: number[];
  onDismiss?: () => void;
  onSuccessfulSubmit: () => void;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
};
const INTERESTS_FIELD = 'interests';
export const InterestsFormModalDialog = t0 => {
  const $ = _c(87);
  const {
    allTopics,
    cmsContent,
    customerInterests,
    onDismiss,
    onSuccessfulSubmit,
    setIsOpen
  } = t0;
  const {
    cancelButtonLabel,
    interestModalSubTitle,
    interestModalTitle,
    interestsErrorMessage,
    saveButtonLabel
  } = cmsContent;
  const [errorMessage, setErrorMessage] = useState();
  let t1;
  if ($[0] !== allTopics || $[1] !== customerInterests) {
    t1 = customerInterests ? allTopics.filter(topic => customerInterests.includes(topic.topicId)) : [];
    $[0] = allTopics;
    $[1] = customerInterests;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = {
      ...formBaseConfig,
      defaultValues: {
        interests: t1
      }
    };
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const formMethods = useForm(t2);
  let t3;
  if ($[5] !== formMethods.control) {
    t3 = {
      control: formMethods.control,
      name: INTERESTS_FIELD
    };
    $[5] = formMethods.control;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    append,
    fields,
    remove
  } = useFieldArray(t3);
  let fieldTopicIds;
  let t4;
  let t5;
  if ($[7] !== fields) {
    fieldTopicIds = fields.map(_temp);
    t4 = useState;
    t5 = new Set(fieldTopicIds);
    $[7] = fields;
    $[8] = fieldTopicIds;
    $[9] = t4;
    $[10] = t5;
  } else {
    fieldTopicIds = $[8];
    t4 = $[9];
    t5 = $[10];
  }
  const [selectedKeys, setSelectedKeys] = t4(t5);
  let t6;
  if ($[11] !== fields) {
    t6 = fields.map(_temp2);
    $[11] = fields;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = {
      initialItems: t6
    };
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const selectedInterests = useListData(t7);
  let t8;
  if ($[15] !== allTopics) {
    t8 = allTopics.map(_temp3);
    $[15] = allTopics;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = {
      initialItems: t8
    };
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const allInterests = useListData(t9);
  const shouldDisableSubmitButton = fields.length < 3;
  let t10;
  if ($[19] !== allInterests || $[20] !== append || $[21] !== fields || $[22] !== remove || $[23] !== selectedInterests) {
    t10 = newKeys => {
      const selectedTopics = Array.from(newKeys);
      const newTopics = selectedTopics.filter(key => !fields.some(t11 => {
        const {
          topicId: topicId_2
        } = t11;
        return topicId_2 === key;
      })).map(key_0 => allInterests.getItem(key_0)).filter(_temp4);
      const removedFieldIndexes = fields.map((t12, index) => {
        const {
          topicId: topicId_3
        } = t12;
        return selectedTopics.includes(topicId_3) ? null : index;
      }).filter(_temp5);
      if (removedFieldIndexes) {
        remove(removedFieldIndexes);
        for (const {
          id
        } of selectedInterests.items.filter(item_0 => !selectedTopics.includes(item_0.id))) {
          selectedInterests.remove(id);
        }
      }
      append(newTopics.map(_temp6));
      selectedInterests.append(...newTopics);
    };
    $[19] = allInterests;
    $[20] = append;
    $[21] = fields;
    $[22] = remove;
    $[23] = selectedInterests;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const saveInterests = t10;
  let t11;
  if ($[25] !== errorMessage || $[26] !== saveInterests) {
    t11 = newKeys_0 => {
      const selectedSize = newKeys_0 instanceof Set ? newKeys_0.size : 0;
      setSelectedKeys(newKeys_0);
      saveInterests(newKeys_0);
      if (selectedSize >= 3 && errorMessage) {
        setErrorMessage(undefined);
      }
    };
    $[25] = errorMessage;
    $[26] = saveInterests;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const handleSelectionChange = t11;
  const {
    personId
  } = useUserContext();
  const addErrorToast = useAddDefaultErrorToast();
  const {
    reset
  } = formMethods;
  let t12;
  if ($[28] !== errorMessage || $[29] !== fieldTopicIds || $[30] !== formMethods || $[31] !== onDismiss || $[32] !== reset || $[33] !== setIsOpen) {
    t12 = () => {
      if (errorMessage) {
        setErrorMessage(undefined);
      }
      if (onDismiss) {
        onDismiss();
      }
      reset({
        ...formMethods.getValues()
      });
      setSelectedKeys(new Set(fieldTopicIds));
      setIsOpen(false);
    };
    $[28] = errorMessage;
    $[29] = fieldTopicIds;
    $[30] = formMethods;
    $[31] = onDismiss;
    $[32] = reset;
    $[33] = setIsOpen;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const cancelSelection = t12;
  let t13;
  if ($[35] !== addErrorToast) {
    t13 = result => {
      if (result.status !== 200 && "message" in result) {
        addErrorToast();
      }
    };
    $[35] = addErrorToast;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  const onFailure = t13;
  let t14;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [AccountSettingsMutationKey.PATCH_ACCOUNT_SETTINGS];
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== formMethods || $[39] !== onSuccessfulSubmit || $[40] !== reset || $[41] !== setIsOpen) {
    t15 = () => {
      reset({
        ...formMethods.getValues()
      });
      setIsOpen(false);
      onSuccessfulSubmit();
    };
    $[38] = formMethods;
    $[39] = onSuccessfulSubmit;
    $[40] = reset;
    $[41] = setIsOpen;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== onFailure || $[44] !== t15) {
    t16 = {
      mutationFunction: patchAccountSettingsAction,
      mutationKey: t14,
      onFailure,
      onSuccessHandler: t15,
      showSuccessToast: false
    };
    $[43] = onFailure;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  const {
    isMutationPending,
    mutate
  } = useMutationHandler(t16);
  let t17;
  if ($[46] !== interestsErrorMessage || $[47] !== mutate || $[48] !== personId) {
    t17 = async data => {
      if (data.interests.length < 3) {
        setErrorMessage(interestsErrorMessage);
        return;
      }
      mutate({
        formData: {
          accountSettingStep: AccountSettingsFormStep.Interests,
          sitePreferenceData: {
            interests: data.interests && data.interests.map(_temp7)
          }
        },
        personId
      });
      const selctedTopicInterests = data?.interests?.map(_temp8).join(", ");
      const topicChunks = splitByCharacterLimit(selctedTopicInterests, 100);
      for (const chunk of topicChunks) {
        sendGtmEvent({
          action: GTM_ACTION_CLICK,
          event: GTM_EVENT_USER_INTERACTIONS,
          eventName: GTM_EVENT_TOPIC_INTEREST_CLICK,
          label: chunk
        });
      }
    };
    $[46] = interestsErrorMessage;
    $[47] = mutate;
    $[48] = personId;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  const handleSubmit = t17;
  let t18;
  if ($[50] !== saveButtonLabel || $[51] !== shouldDisableSubmitButton) {
    t18 = <Button disabled={shouldDisableSubmitButton} type={ButtonType.SUBMIT}>{saveButtonLabel}</Button>;
    $[50] = saveButtonLabel;
    $[51] = shouldDisableSubmitButton;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  let t19;
  if ($[53] !== cancelButtonLabel || $[54] !== cancelSelection) {
    t19 = <Button onClick={cancelSelection} variant={ButtonVariant.Secondary}>{cancelButtonLabel}</Button>;
    $[53] = cancelButtonLabel;
    $[54] = cancelSelection;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== t18 || $[57] !== t19) {
    t20 = <div className={styles["interests-modal-controls"]}>{t18}{t19}</div>;
    $[56] = t18;
    $[57] = t19;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = classNames(styles["interests-modal-controls"], styles["interests-modal-controls--secondary-layout"]);
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] !== cancelButtonLabel || $[61] !== cancelSelection) {
    t22 = <Button onClick={cancelSelection} variant={ButtonVariant.Secondary}>{cancelButtonLabel}</Button>;
    $[60] = cancelButtonLabel;
    $[61] = cancelSelection;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== saveButtonLabel || $[64] !== shouldDisableSubmitButton) {
    t23 = <Button disabled={shouldDisableSubmitButton} type={ButtonType.SUBMIT}>{saveButtonLabel}</Button>;
    $[63] = saveButtonLabel;
    $[64] = shouldDisableSubmitButton;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== t22 || $[67] !== t23) {
    t24 = <div className={t21}>{t22}{t23}</div>;
    $[66] = t22;
    $[67] = t23;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  let t25;
  if ($[69] !== t20 || $[70] !== t24) {
    t25 = <>{t20}{t24}</>;
    $[69] = t20;
    $[70] = t24;
    $[71] = t25;
  } else {
    t25 = $[71];
  }
  let t26;
  if ($[72] !== allInterests.items || $[73] !== errorMessage || $[74] !== handleSelectionChange || $[75] !== selectedKeys) {
    t26 = <div className={styles["interests-modal-content"]}><TagsList aria-labelledby="interest-modal-title" centerContent={true} errorMessage={errorMessage} items={allInterests.items} onSelectionChange={handleSelectionChange} selectedKeys={selectedKeys} selectionMode="multiple" /></div>;
    $[72] = allInterests.items;
    $[73] = errorMessage;
    $[74] = handleSelectionChange;
    $[75] = selectedKeys;
    $[76] = t26;
  } else {
    t26 = $[76];
  }
  let t27;
  if ($[77] !== interestModalSubTitle || $[78] !== interestModalTitle || $[79] !== isMutationPending || $[80] !== t25 || $[81] !== t26) {
    t27 = <Dialog footer={t25} footerClassName={styles["interests-modal-footer"]} hasCloseButton={false} headerClassName={styles["interests-modal-header"]} isLoading={isMutationPending} subtitle={interestModalSubTitle} title={interestModalTitle} titleId="interest-modal-title">{t26}</Dialog>;
    $[77] = interestModalSubTitle;
    $[78] = interestModalTitle;
    $[79] = isMutationPending;
    $[80] = t25;
    $[81] = t26;
    $[82] = t27;
  } else {
    t27 = $[82];
  }
  let t28;
  if ($[83] !== formMethods || $[84] !== handleSubmit || $[85] !== t27) {
    t28 = <FormWrapper className={styles["site-preferences-form"]} dataTestId="interests-preferences-form" formMethods={formMethods} onSubmit={handleSubmit}>{t27}</FormWrapper>;
    $[83] = formMethods;
    $[84] = handleSubmit;
    $[85] = t27;
    $[86] = t28;
  } else {
    t28 = $[86];
  }
  return t28;
};
function _temp(t0) {
  const {
    topicId
  } = t0;
  return topicId;
}
function _temp2(t0) {
  const {
    name,
    topicId: topicId_0
  } = t0;
  return {
    id: topicId_0,
    name
  };
}
function _temp3(t0) {
  const {
    name: name_0,
    topicId: topicId_1
  } = t0;
  return {
    id: topicId_1,
    name: name_0
  };
}
function _temp4(item) {
  return item !== undefined;
}
function _temp5(index_0) {
  return index_0 !== null;
}
function _temp6(t0) {
  const {
    id: id_0,
    name: name_1
  } = t0;
  return {
    name: name_1,
    topicId: id_0
  };
}
function _temp7(interest) {
  return interest.topicId;
}
function _temp8(interest_0) {
  return interest_0?.name;
}